// Moving content between decks.
//
// One window does two opposite jobs — take part of this deck out, and bring
// part of another one in — and the old layout asked the author to hold both in
// their head at once: two tab strips, a pill toggle, a tree of nested cards,
// and the buttons that finish the job scrolled away at the bottom of the list.
//
// The rebuild keeps every piece of state the deck already had and changes what
// the author sees:
//
//   * one question per step, in the order the job is actually done;
//   * one number per slide, and it is the number the deck itself shows — the
//     position in the running deck, not the index inside its section, which is
//     what made "Slide 3" appear five times in one list;
//   * a standing answer to "where will this land?", drawn as the slides either
//     side of the insertion point, so the placement controls are read as a
//     result rather than as a form.
//
// The footer never scrolls: the count of what is selected and the button that
// acts on it are always in the same place.
import React, { useEffect, useMemo, useRef } from 'react';
import { X, Upload, FileDown, Layers, LayoutGrid, ArrowRight, CornerDownRight } from 'lucide-react';

/** A checkbox that can also say "some of these", which a bare input cannot. */
const TriCheckbox = ({ checked, indeterminate, onChange, onClick, label, className = '' }) => {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = Boolean(indeterminate && !checked);
  }, [indeterminate, checked]);
  return (
    <input
      ref={ref}
      type="checkbox"
      checked={Boolean(checked)}
      onChange={onChange}
      // Click rather than change, for the rows that read the Shift key: change
      // carries no modifier state, and the keyboard's Space fires click too.
      onClick={onClick}
      aria-label={label}
      className={`h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 ${className}`}
    />
  );
};

/**
 * The deck position, printed the same way everywhere in this window.
 *
 * Tabular figures and a fixed minimum width so a column of them lines up, and
 * so slide 9 and slide 10 do not sit at different left edges.
 */
const DeckNumber = ({ number, tone = 'deck' }) => (
  <span
    className={`inline-flex min-w-[2.25rem] justify-center rounded-md px-1.5 py-0.5 text-sm font-semibold tabular-nums ${
      tone === 'incoming'
        ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
        : 'bg-slate-100 text-slate-700 border border-slate-200'
    }`}
  >
    {number}
  </span>
);

/** One of the two things this window can do, as a card rather than a pill. */
const ChoiceCard = ({ active, onClick, icon: Icon, title, description }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`flex-1 min-w-[15rem] text-left rounded-xl border p-3 transition-colors ${
      active
        ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
        : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50'
    }`}
  >
    <span className={`flex items-center gap-2 text-sm font-semibold ${active ? 'text-blue-800' : 'text-gray-800'}`}>
      <Icon size={16} />
      {title}
    </span>
    <span className="mt-1 block text-xs leading-relaxed text-gray-600">{description}</span>
  </button>
);

/**
 * The frame a thumbnail sits in: 16:9 at a size that keeps a whole section's
 * worth of rows on screen. The miniature scales itself to whatever box it is
 * given, so this is the only place the size is decided.
 */
const THUMB_BOX = { width: '5.5rem', height: '3.09rem' };

const TITLE_CARD_LABELS = {
  start: 'Title card',
  section: 'Section title',
  end: 'End title card'
};

/**
 * A title card in the list.
 *
 * It carries a deck number like every other slide — leaving it out was what
 * made the numbering jump — but no checkbox: a title card's words live in its
 * own settings rather than in blocks, so a selection file has nothing to carry
 * and importing one would land an empty slide.
 */
const TitleCardRow = ({ card, renderThumbnail }) => (
  <div className="flex items-center gap-3 bg-gray-50/70 px-3 py-2">
    <span className="h-4 w-4 flex-none" aria-hidden="true" />
    <DeckNumber number={card.deckNumber} />
    {renderThumbnail && (
      <span className="hidden flex-none overflow-hidden rounded border border-gray-200 sm:block" style={THUMB_BOX}>
        {renderThumbnail(card)}
      </span>
    )}
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-gray-700">{card.title}</p>
      <p className="text-xs text-gray-500">
        {TITLE_CARD_LABELS[card.titleCardKind] || 'Title card'} · travels with the deck, not with a selection
      </p>
    </div>
  </div>
);

const StepHeading = ({ step, title, hint, done }) => (
  <div className="flex items-start gap-3">
    <span
      className={`mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full text-xs font-bold ${
        done ? 'bg-emerald-600 text-white' : 'bg-slate-800 text-white'
      }`}
    >
      {step}
    </span>
    <div className="min-w-0">
      <p className="text-sm font-semibold text-gray-900">{title}</p>
      {hint && <p className="text-xs text-gray-600">{hint}</p>}
    </div>
  </div>
);

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const TransferContentModal = ({
  open,
  mode,
  onModeChange,
  onClose,

  // Export
  exportType,
  onExportTypeChange,
  deckSections,
  deckEdges = {},
  renderThumbnail,
  selectedBlockIds,
  onToggleBlock,
  onSetBlocks,
  selectedSlideIds,
  onToggleSlide,
  onSetSlides,
  collapsedSections,
  onToggleSectionCollapse,
  getBlockPreview,
  onExport,

  // Import
  onChooseFile,
  fileInput,
  importData,
  importSlides,
  importSelectionSlideIds,
  onToggleImportSlide,
  onSetImportSlides,
  targetSectionId,
  onTargetSectionChange,
  placementMode,
  onPlacementModeChange,
  placementSlideId,
  onPlacementSlideChange,
  blockTargetSlideId,
  onBlockTargetChange,
  onImport
}) => {
  const isExport = mode === 'export';
  // Where a Shift-click measures from.
  const anchorRef = useRef(null);

  // Every row in the list, title cards included — they are here to explain the
  // numbering, not to be exported.
  const allRows = useMemo(
    () => deckSections.flatMap((section) => section.slides),
    [deckSections]
  );

  const allSlides = useMemo(
    () => allRows.filter((row) => row.kind !== 'titleCard'),
    [allRows]
  );

  const selectedSlideCount = useMemo(
    () => allSlides.filter((slide) => selectedSlideIds[slide.slideId]).length,
    [allSlides, selectedSlideIds]
  );

  const selectedBlockCount = useMemo(
    () => allSlides.reduce(
      (sum, slide) => sum + slide.blocks.filter((block) => selectedBlockIds[block.id]).length,
      0
    ),
    [allSlides, selectedBlockIds]
  );

  const exportCount = exportType === 'slides' ? selectedSlideCount : selectedBlockCount;

  // Where the incoming content lands, worked out once and used both for the
  // sentence and for the drawing below it.
  const destination = useMemo(() => {
    if (!importData) return null;
    const section = deckSections.find((entry) => entry.id === targetSectionId) || deckSections[0];
    if (!section) return null;

    const incomingCount = importData.type === 'slides'
      ? importSlides.filter((slide) => importSelectionSlideIds[slide.slideId]).length
      : (importData.blocks?.length || 0);

    if (importData.type === 'blocks') {
      const sectionSlides = (section.slides || []).filter((slide) => slide.kind !== 'titleCard');
      const targetSlide = blockTargetSlideId && blockTargetSlideId !== '__new__'
        ? sectionSlides.find((slide) => slide.slideId === blockTargetSlideId)
        : null;
      return {
        kind: 'blocks',
        section,
        incomingCount,
        targetSlide,
        newSlide: !targetSlide,
        // A new slide is added at the end of the section, so it takes the
        // position after that section's last slide.
        newSlideNumber: sectionSlides.length
          ? sectionSlides[sectionSlides.length - 1].deckNumber + 1
          : null
      };
    }

    const sectionSlides = (section.slides || []).filter((slide) => slide.kind !== 'titleCard');
    const anchorIndex = placementMode === 'append'
      ? sectionSlides.length - 1
      : sectionSlides.findIndex((slide) => slide.slideId === placementSlideId);
    const resolvedAnchor = anchorIndex >= 0 ? anchorIndex : sectionSlides.length - 1;
    // "before" lands on the anchor's own position; "after" and "append" land
    // one past it.
    const insertAt = placementMode === 'before' ? resolvedAnchor : resolvedAnchor + 1;

    return {
      kind: 'slides',
      section,
      incomingCount,
      before: sectionSlides.slice(Math.max(0, insertAt - 2), insertAt),
      after: sectionSlides.slice(insertAt, insertAt + 2),
      firstNumber: sectionSlides[insertAt]?.deckNumber
        ?? ((sectionSlides[sectionSlides.length - 1]?.deckNumber ?? 0) + 1)
    };
  }, [
    importData, importSlides, importSelectionSlideIds, deckSections,
    targetSectionId, placementMode, placementSlideId, blockTargetSlideId
  ]);

  const importCount = destination?.incomingCount || 0;

  // Escape closes, as it does for every other overlay in the app.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const setEveryBlockInSlide = (slide, value) => {
    onSetBlocks(slide.blocks.map((block) => block.id), value);
  };

  /**
   * One slide, or a run of them.
   *
   * Ticking boxes one at a time is fine for three slides and miserable for
   * thirty, so Shift takes everything between the last row touched and this
   * one — the gesture every file list has. Without an anchor it is an ordinary
   * click, and every path leaves this row as the next anchor.
   */
  const pickSlide = (slide, event) => {
    const anchorId = anchorRef.current;
    anchorRef.current = slide.slideId;
    if (!event?.shiftKey || !anchorId || anchorId === slide.slideId) {
      onToggleSlide(slide.slideId);
      return;
    }
    const from = allSlides.findIndex((entry) => entry.slideId === anchorId);
    const to = allSlides.findIndex((entry) => entry.slideId === slide.slideId);
    if (from < 0 || to < 0) {
      onToggleSlide(slide.slideId);
      return;
    }
    const run = allSlides.slice(Math.min(from, to), Math.max(from, to) + 1);
    // A range follows the row that started it: extending from a ticked slide
    // ticks the run, extending from a cleared one clears it.
    onSetSlides(run.map((entry) => entry.slideId), !selectedSlideIds[slide.slideId]);
  };

  const contentSlides = (section) => (section?.slides || []).filter((slide) => slide.kind !== 'titleCard');

  const sectionSelectionState = (section) => {
    const pickable = contentSlides(section);
    if (exportType === 'slides') {
      const picked = pickable.filter((slide) => selectedSlideIds[slide.slideId]).length;
      return { picked, total: pickable.length };
    }
    const blocks = pickable.flatMap((slide) => slide.blocks);
    return { picked: blocks.filter((block) => selectedBlockIds[block.id]).length, total: blocks.length };
  };

  const toggleSection = (section) => {
    const { picked, total } = sectionSelectionState(section);
    const value = picked < total;
    const pickable = contentSlides(section);
    if (exportType === 'slides') {
      onSetSlides(pickable.map((slide) => slide.slideId), value);
    } else {
      onSetBlocks(pickable.flatMap((slide) => slide.blocks).map((block) => block.id), value);
    }
  };

  const selectEverything = (value) => {
    if (exportType === 'slides') {
      onSetSlides(allSlides.map((slide) => slide.slideId), value);
    } else {
      onSetBlocks(allSlides.flatMap((slide) => slide.blocks).map((block) => block.id), value);
    }
  };

  const renderExport = () => (
    <div className="space-y-5">
      <div className="space-y-3">
        <StepHeading step="1" title="What do you want to take out?" />
        <div className="flex flex-wrap gap-3 pl-9">
          <ChoiceCard
            active={exportType === 'slides'}
            onClick={() => onExportTypeChange('slides')}
            icon={Layers}
            title="Whole slides"
            description="Each slide leaves with everything on it. Best for reusing a slide in another deck."
          />
          <ChoiceCard
            active={exportType === 'blocks'}
            onClick={() => onExportTypeChange('blocks')}
            icon={LayoutGrid}
            title="Individual blocks"
            description="Pick single pieces of content to drop onto a slide that already exists elsewhere."
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <StepHeading
            step="2"
            title={exportType === 'slides' ? 'Which slides?' : 'Which blocks?'}
            hint="Numbers are the slide's place in this deck — the same number the deck shows while you present."
          />
          <div className="flex items-center gap-2 text-xs font-semibold">
            <button type="button" onClick={() => selectEverything(true)} className="rounded-md px-2 py-1 text-blue-700 hover:bg-blue-50">
              Select all
            </button>
            <button type="button" onClick={() => selectEverything(false)} className="rounded-md px-2 py-1 text-gray-600 hover:bg-gray-100">
              Clear
            </button>
          </div>
        </div>

        <div className="space-y-3">
          {deckEdges.start && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <TitleCardRow card={deckEdges.start} renderThumbnail={renderThumbnail} />
            </div>
          )}
          {deckSections.map((section) => {
            const { picked, total } = sectionSelectionState(section);
            const collapsed = Boolean(collapsedSections[section.id]);
            return (
              <section key={section.id} className="overflow-hidden rounded-xl border border-gray-200">
                <header className="flex items-center gap-3 bg-gray-50 px-3 py-2">
                  <TriCheckbox
                    checked={total > 0 && picked === total}
                    indeterminate={picked > 0}
                    onChange={() => toggleSection(section)}
                    label={`Select everything in ${section.title}`}
                  />
                  <button
                    type="button"
                    onClick={() => onToggleSectionCollapse(section.id)}
                    className="min-w-0 flex-1 text-left"
                    aria-expanded={!collapsed}
                  >
                    <span className="block truncate text-sm font-semibold text-gray-900">{section.title}</span>
                    <span className="block text-xs text-gray-600">
                      {plural(contentSlides(section).length, 'slide')}
                      {exportType === 'blocks' && ` · ${plural(total, 'block')}`}
                      {picked > 0 && <span className="ml-1 font-semibold text-blue-700">· {picked} selected</span>}
                    </span>
                  </button>
                  <span className="text-xs text-gray-500">{collapsed ? 'Show' : 'Hide'}</span>
                </header>

                {!collapsed && (
                  <div className="divide-y divide-gray-100">
                    {section.slides.length === 0 && (
                      <p className="px-3 py-3 text-xs italic text-gray-500">No slides in this section yet.</p>
                    )}
                    {section.slides.map((slide) => {
                      if (slide.kind === 'titleCard') return <TitleCardRow key={slide.slideId} card={slide} renderThumbnail={renderThumbnail} />;
                      const slidePicked = slide.blocks.filter((block) => selectedBlockIds[block.id]).length;
                      return (
                        <div key={slide.slideId}>
                          <div className="flex items-start gap-3 px-3 py-2">
                            {exportType === 'slides' ? (
                              <TriCheckbox
                                checked={Boolean(selectedSlideIds[slide.slideId])}
                                onChange={() => {}}
                                onClick={(event) => pickSlide(slide, event)}
                                label={`Select slide ${slide.deckNumber}`}
                                className="mt-1"
                              />
                            ) : (
                              <TriCheckbox
                                checked={slide.blocks.length > 0 && slidePicked === slide.blocks.length}
                                indeterminate={slidePicked > 0}
                                onChange={() => setEveryBlockInSlide(slide, slidePicked < slide.blocks.length)}
                                label={`Select every block on slide ${slide.deckNumber}`}
                                className="mt-1"
                              />
                            )}
                            <DeckNumber number={slide.deckNumber} />
                            {renderThumbnail && (
                              <span className="hidden flex-none overflow-hidden rounded border border-gray-200 bg-white sm:block" style={THUMB_BOX}>
                                {renderThumbnail(slide)}
                              </span>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">{slide.title}</p>
                              <p className="text-xs text-gray-500">
                                {slide.sectionRef && <span className="tabular-nums">{slide.sectionRef}</span>}
                                {slide.sectionRef && ' · '}
                                {plural(slide.blocks.length, 'block')}
                              </p>
                            </div>
                          </div>

                          {exportType === 'blocks' && slide.blocks.length > 0 && (
                            <ul className="space-y-1 border-t border-gray-100 bg-gray-50/60 px-3 py-2 pl-12">
                              {slide.blocks.map((block, index) => (
                                <li key={block.id}>
                                  <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2">
                                    <TriCheckbox
                                      checked={Boolean(selectedBlockIds[block.id])}
                                      onChange={() => onToggleBlock(block.id)}
                                      label={`Select block ${index + 1} on slide ${slide.deckNumber}`}
                                      className="mt-0.5"
                                    />
                                    <span className="min-w-0">
                                      <span className="flex items-baseline gap-2">
                                        <span className="text-xs font-semibold tabular-nums text-gray-500">
                                          Block {index + 1} of {slide.blocks.length}
                                        </span>
                                        <span className="text-sm font-medium capitalize text-gray-900">
                                          {(block.type || 'block').replace(/[-_]/g, ' ')}
                                        </span>
                                      </span>
                                      <span className="mt-0.5 block truncate text-xs text-gray-600">
                                        {getBlockPreview(block)}
                                      </span>
                                    </span>
                                  </label>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </section>
            );
          })}
          {deckEdges.end && (
            <div className="overflow-hidden rounded-xl border border-gray-200">
              <TitleCardRow card={deckEdges.end} renderThumbnail={renderThumbnail} />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const renderDestinationPreview = () => {
    if (!destination) return null;

    if (destination.kind === 'blocks') {
      return (
        <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
          <p className="text-sm font-semibold text-emerald-900">
            {plural(destination.incomingCount, 'block')} will be added to the end of{' '}
            {destination.targetSlide
              ? <>slide {destination.targetSlide.deckNumber} — {destination.targetSlide.title}</>
              : <>a new slide at the end of {destination.section.title}</>}
          </p>
          {destination.newSlide && destination.newSlideNumber && (
            <p className="mt-1 text-xs text-emerald-800">
              The new slide becomes slide {destination.newSlideNumber}.
            </p>
          )}
        </div>
      );
    }

    return (
      <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-sm font-semibold text-emerald-900">
          {destination.incomingCount > 0
            ? (
              <>
                {plural(destination.incomingCount, 'slide')}
                {destination.incomingCount === 1 ? ' lands' : ' land'} in {destination.section.title},
                {' '}starting at slide {destination.firstNumber}.
              </>
            )
            : <>Nothing is selected to import yet.</>}
        </p>
        {destination.incomingCount > 0 && (
          <ol className="mt-2 space-y-1">
            {destination.before.map((slide) => (
              <li key={slide.slideId} className="flex items-center gap-2 text-xs text-emerald-900/70">
                <DeckNumber number={slide.deckNumber} />
                <span className="truncate">{slide.title}</span>
              </li>
            ))}
            <li className="flex items-center gap-2 text-xs font-semibold text-emerald-900">
              <CornerDownRight size={14} className="flex-none" />
              <DeckNumber number={destination.firstNumber} tone="incoming" />
              <span className="truncate">
                your {plural(destination.incomingCount, 'slide')} go here
              </span>
            </li>
            {destination.after.map((slide) => (
              <li key={slide.slideId} className="flex items-center gap-2 text-xs text-emerald-900/70">
                <DeckNumber number={slide.deckNumber + destination.incomingCount} />
                <span className="truncate">{slide.title}</span>
                <span className="flex-none text-[11px] text-emerald-900/50">was {slide.deckNumber}</span>
              </li>
            ))}
          </ol>
        )}
      </div>
    );
  };

  const renderImport = () => {
    const targetSection = deckSections.find((entry) => entry.id === targetSectionId) || deckSections[0];
    const sectionSlides = (targetSection?.slides || []).filter((slide) => slide.kind !== 'titleCard');
    const pickedImport = importSlides.filter((slide) => importSelectionSlideIds[slide.slideId]).length;

    return (
      <div className="space-y-5">
        <div className="space-y-3">
          <StepHeading step="1" title="Choose the file to bring in" done={Boolean(importData)} />
          <div className="flex flex-wrap items-center gap-3 pl-9">
            <button
              type="button"
              onClick={onChooseFile}
              className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700"
            >
              <Upload size={16} />
              {importData ? 'Choose a different file' : 'Choose file'}
            </button>
            {fileInput}
            {importData && (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-sm font-medium text-emerald-800">
                {importData.type === 'slides'
                  ? `${plural(importSlides.length, 'slide')} in this file`
                  : `${plural(importData.blocks?.length || 0, 'block')} in this file`}
              </span>
            )}
          </div>
        </div>

        {!importData && (
          <p className="pl-9 text-sm text-gray-600">
            Pick a file that was exported from a deck with this same window. The rest of the steps
            appear once it is loaded.
          </p>
        )}

        {importData?.type === 'slides' && (
          <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <StepHeading
                step="2"
                title="Which slides do you want?"
                hint="Numbered by their order inside the file, not by where they will end up."
                done={pickedImport > 0}
              />
              <div className="flex items-center gap-2 text-xs font-semibold">
                <button
                  type="button"
                  onClick={() => onSetImportSlides(importSlides.map((slide) => slide.slideId), true)}
                  className="rounded-md px-2 py-1 text-blue-700 hover:bg-blue-50"
                >
                  Select all
                </button>
                <button
                  type="button"
                  onClick={() => onSetImportSlides(importSlides.map((slide) => slide.slideId), false)}
                  className="rounded-md px-2 py-1 text-gray-600 hover:bg-gray-100"
                >
                  Clear
                </button>
              </div>
            </div>
            <ul className="divide-y divide-gray-100 overflow-hidden rounded-xl border border-gray-200">
              {importSlides.map((slide, index) => (
                <li key={slide.slideId}>
                  <label className="flex cursor-pointer items-start gap-3 px-3 py-2 hover:bg-gray-50">
                    <TriCheckbox
                      checked={Boolean(importSelectionSlideIds[slide.slideId])}
                      onChange={() => onToggleImportSlide(slide.slideId)}
                      label={`Import file slide ${index + 1}`}
                      className="mt-1"
                    />
                    <DeckNumber number={index + 1} tone="incoming" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-gray-900">
                        {slide.title || 'Untitled slide'}
                      </span>
                      <span className="block truncate text-xs text-gray-500">
                        from {slide.sectionTitle || 'an unnamed section'}
                      </span>
                    </span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {importData && (
          <div className="space-y-3">
            <StepHeading
              step={importData.type === 'slides' ? '3' : '2'}
              title="Where should it go?"
            />
            <div className="space-y-4 pl-9">
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Section</span>
                <select
                  value={targetSectionId || deckSections[0]?.id || ''}
                  onChange={(event) => onTargetSectionChange(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 md:w-80"
                >
                  {deckSections.map((section) => (
                    <option key={section.id} value={section.id}>
                      {section.title} ({plural(contentSlides(section).length, 'slide')})
                    </option>
                  ))}
                </select>
              </label>

              {importData.type === 'blocks' ? (
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-gray-500">Slide to add them to</span>
                  <select
                    value={blockTargetSlideId || '__new__'}
                    onChange={(event) => onBlockTargetChange(event.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 md:w-96"
                  >
                    {sectionSlides.map((slide) => (
                      <option key={slide.slideId} value={slide.slideId}>
                        {`Slide ${slide.deckNumber} — ${slide.title}`}
                      </option>
                    ))}
                    <option value="__new__">Add a new slide at the end of this section</option>
                  </select>
                </label>
              ) : (
                <div className="space-y-2">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-gray-500">Position</span>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { value: 'append', label: 'At the end of the section' },
                      { value: 'before', label: 'Before a slide' },
                      { value: 'after', label: 'After a slide' }
                    ].map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => onPlacementModeChange(option.value)}
                        className={`rounded-full border px-3 py-1 text-sm ${
                          placementMode === option.value
                            ? 'border-blue-600 bg-blue-600 text-white'
                            : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                  {placementMode !== 'append' && (
                    <select
                      value={placementSlideId || ''}
                      onChange={(event) => onPlacementSlideChange(event.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500 md:w-96"
                    >
                      <option value="">Choose a slide…</option>
                      {sectionSlides.map((slide) => (
                        <option key={slide.slideId} value={slide.slideId}>
                          {`Slide ${slide.deckNumber} — ${slide.title}`}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              {renderDestinationPreview()}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[90vh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
        <header className="border-b border-gray-200 px-6 pt-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Move content between decks</h2>
              <p className="text-sm text-gray-600">
                {isExport
                  ? 'Take slides or blocks out of this deck as a file you can load into another one.'
                  : 'Bring slides or blocks from another deck into this one.'}
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
          <div className="mt-4 flex gap-1" role="tablist" aria-label="Transfer direction">
            {[
              { value: 'export', label: 'Export from this deck' },
              { value: 'import', label: 'Import into this deck' }
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                role="tab"
                aria-selected={mode === tab.value}
                onClick={() => onModeChange(tab.value)}
                className={`rounded-t-lg border-b-2 px-4 py-2 text-sm font-semibold transition-colors ${
                  mode === tab.value
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {isExport ? renderExport() : renderImport()}
        </div>

        <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-200 bg-gray-50 px-6 py-3">
          <p className="text-sm text-gray-700">
            {isExport
              ? exportCount > 0
                ? <><span className="font-semibold">{plural(exportCount, exportType === 'slides' ? 'slide' : 'block')}</span> ready to export</>
                : 'Nothing selected yet'
              : importCount > 0
                ? <><span className="font-semibold">{plural(importCount, importData?.type === 'slides' ? 'slide' : 'block')}</span> ready to import</>
                : 'Nothing selected yet'}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200"
            >
              Cancel
            </button>
            {isExport ? (
              <button
                type="button"
                onClick={onExport}
                disabled={exportCount === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
              >
                <FileDown size={16} />
                Export {exportCount > 0 ? plural(exportCount, exportType === 'slides' ? 'slide' : 'block') : 'selection'}
              </button>
            ) : (
              <button
                type="button"
                onClick={onImport}
                disabled={importCount === 0}
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 disabled:text-gray-600"
              >
                <ArrowRight size={16} />
                Add to this deck
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default TransferContentModal;
